import { request } from './core';

export type BookingStatus = "inquiry" | "booked" | "processing" | "completed" | "cancelled";

export interface BookingActivity {
  ID: string;
  BookingID: string;
  UserID: string;
  Kind: "note" | "status_change" | "nudge";
  Body: string;
  CreatedAt: string;
}

export interface BookingSettings {
  booking_archive_days: number;
  nudge_stale_days: number;
}

export interface BookingLineItem {
  ID: string;
  BookingID: string;
  PackageID: string;        // empty = ad-hoc
  PackageName: string;
  Name: string;
  Description: string;
  PriceCents: number;
  Currency: string;
  Quantity: number;
  SortOrder: number;
  CreatedAt: string;
}

export interface Booking {
  ID: string;
  UserID: string;
  ClientID: string;
  PackageID: string;
  Title: string;
  Description: string;
  Status: BookingStatus;
  Location: string;
  ScheduledAt: string | null;
  DurationMin: number;
  PackageName: string;
  PriceCents: number;
  Currency: string;
  DepositCents: number;
  DepositPaid: boolean;
  BalancePaid: boolean;
  Notes: string;
  ClientNotes: string;
  DeliveryExpectedAt: string | null;
  ArchivedAt: string | null;
  LineItems?: BookingLineItem[];
  CreatedAt: string;
  UpdatedAt: string;
}

export interface InquiryResult {
  ClientID: string;
  ClientName: string;
  Contact: string;
  BookingID: string;
}

export const bookingsApi = {
  getBooking: (id: string) =>
    request<Booking>("GET", `/api/v1/bookings/${encodeURIComponent(id)}`),
  listBookings: (status?: BookingStatus, clientId?: string, includeArchived?: boolean) => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (clientId) params.set("client_id", clientId);
    if (includeArchived) params.set("include_archived", "true");
    const qs = params.toString();
    return request<Booking[]>("GET", `/api/v1/bookings${qs ? `?${qs}` : ""}`);
  },
  updateBooking: (
    id: string,
    patch: {
      title?: string;
      description?: string;
      location?: string;
      scheduled_at?: string;
      duration_min?: number;
      package_name?: string;
      package_id?: string | null;
      price_cents?: number;
      currency?: string;
      deposit_cents?: number;
      deposit_paid?: boolean;
      balance_paid?: boolean;
      notes?: string;
      client_notes?: string;
      client_id?: string;
    },
  ) => request<Booking>("PUT", `/api/v1/bookings/${encodeURIComponent(id)}`, patch),
  createBooking: (b: {
    title: string;
    ClientID?: string;
    PackageID?: string;
    Location?: string;
    ScheduledAt?: string;
    DurationMin?: number;
    PriceCents?: number;
    Currency?: string;
    PackageName?: string;
    DepositCents?: number;
    Notes?: string;
  }) =>
    request<Booking>("POST", "/api/v1/bookings", {
      title: b.title,
      description: "",
      client_id: b.ClientID ?? "",
      package_id: b.PackageID ?? "",
      status: "inquiry",
      location: b.Location ?? "",
      scheduled_at: b.ScheduledAt ?? "",
      duration_min: b.DurationMin ?? 0,
      package_name: b.PackageName ?? "",
      price_cents: b.PriceCents ?? 0,
      currency: b.Currency ?? "EUR",
      deposit_cents: b.DepositCents ?? 0,
      notes: b.Notes ?? "",
    }),
  transitionBooking: (id: string, to: BookingStatus) =>
    request<Booking>("POST", `/api/v1/bookings/${id}/transition`, { status: to }),

  archiveBooking: (id: string) =>
    request<Booking>("POST", `/api/v1/bookings/${id}/archive`, {}),

  createInquiry: (body: { caller_name: string; contact: string; description?: string; rough_date?: string; lead_id?: string }) =>
    request<InquiryResult>("POST", "/api/v1/inquiries", body),

  markShootDone: (id: string, body: { delivery_date: string; message: string }) =>
    request<{ booking: Booking; email_sent: boolean; gallery_slug: string }>(
      "POST", `/api/v1/bookings/${id}/mark-shoot-done`, body),

  getBookingLineItems: (bookingId: string) =>
    request<BookingLineItem[]>("GET", `/api/v1/bookings/${bookingId}/line-items`),

  setBookingLineItems: (bookingId: string, items: Array<{
    package_id?: string;
    name: string;
    description?: string;
    price_cents: number;
    currency?: string;
    quantity?: number;
  }>) => request<BookingLineItem[]>("PUT", `/api/v1/bookings/${bookingId}/line-items`, items),

  listBookingActivities: (bookingId: string) =>
    request<BookingActivity[]>("GET", `/api/v1/bookings/${encodeURIComponent(bookingId)}/activities`),
  addBookingNote: (bookingId: string, body: string) =>
    request<BookingActivity>("POST", `/api/v1/bookings/${encodeURIComponent(bookingId)}/activities`, { body }),

  getBookingSettings: () =>
    request<BookingSettings>("GET", "/api/v1/account/booking-settings"),
  updateBookingSettings: (s: { booking_archive_days?: number; nudge_stale_days?: number }) =>
    request<BookingSettings>("PUT", "/api/v1/account/booking-settings", s),
};
